/**
 * Best Practices for Zustand Selector Patterns
 * =============================================
 *
 * When deriving values from memoized selectors, there are two patterns with different
 * performance characteristics:
 *
 * ✅ Subscribe to derived value directly
 *
 * ```typescript
 * // Subscribe to the primative `length` - only re-renders when count changes
 * const useDiffSummaryCount = () =>
 *   usePullRequestStore(s => useDiffSummariesArray.selector(s).length)
 * ```
 *
 * Use this when you need a **primitive value** derived from the selector result.
 * Zustand compares primitives by value (e.g., `300 === 300`), so no re-render occurs
 * if the derived value hasn't changed.
 *
 * ❌ Call hook, then derive (Causes extra re-renders)
 *
 * ```typescript
 * // Subscribe to the ARRAY reference - re-renders when any item changes
 * const useDiffSummaryCount = () => useDiffSummariesArray().length
 * ```
 *
 * This subscribes to the array reference via `useShallow`. Even though `useShallow`
 * does shallow comparison, Immer's `produce()` creates new object references on
 * every state update, causing `useShallow` to detect "changes" and trigger re-renders.
 *
 * ## When to use each pattern:
 *
 * | Need | Pattern | Wrap with |
 * |------|---------|-----------|
 * | Primitive (number, string, boolean) | `usePullRequestStore(s => selector(s).value)` | nothing |
 * | Array/Object (need full data) | Call hook directly: `useMyHook()` | — |
 *
 * ## Composing selectors:
 *
 * Every hook created by `createMemoizedSelectorHook` exposes a `.selector` property
 * that can be composed into other selectors:
 *
 * ```typescript
 * // Create a memoized selector
 * const useDiffSummariesArray = createMemoizedSelectorHook(
 *   [s => s.diffSummaries],
 *   (diffSummaries) => Object.values(diffSummaries)
 * )
 *
 * // Compose it into another selector using .selector
 * const useDiffSummaryCount = () =>
 *   usePullRequestStore(s => useDiffSummariesArray.selector(s).length)
 * ```
 *
 * ## Why this matters:
 *
 * When store actions use Immer's `produce()`, every update creates new object references
 * even for unchanged nested data. Avoid this by comparing the
 * final derived primitive value instead of intermediate object references.
 */

import {shallow, useShallow} from 'zustand/shallow'
import {usePullRequestStore, type State} from './PullRequestStore'

export type Selector<T, U> = (state: T) => U

export type SelectorReturnTypes<T, Selectors extends Array<Selector<T, unknown>>> = {
  [K in keyof Selectors]: Selectors[K] extends Selector<T, infer R> ? R : never
}

/**
 * Creates a memoized selector function that computes derived data from multiple
 * input selectors.  The selector will only recompute when its dependencies
 * change based on the provided comparison function. It's important to note that
 * each time the resulting hook is called, each selector function will be called
 * to compare prev/current values.
 *
 * @template T - The type of the state object
 * @template Selectors - Array of selector functions that extract values from the state
 * @template U - The type of the final computed result
 *
 * @param selectors - Array of selector functions that pick values from the state
 * @param resultFn - Function that computes the final result using the selected values
 * @param compare - Function to determine if selected values have changed
 *
 * @returns A memoized selector function that takes the state and returns the computed result
 *
 * @example
 * const mySelector = createMemoizedSelector(
 *   [state => state.value1, state => state.value2],
 *   (value1, value2) => value1 + value2,
 *   shallow
 * );
 */
export function createMemoizedSelector<T, Selectors extends Array<Selector<T, unknown>>, U>(
  selectors: [...Selectors],
  resultFn: (...args: SelectorReturnTypes<T, Selectors>) => U,
  compare: (a: SelectorReturnTypes<T, Selectors>, b: SelectorReturnTypes<T, Selectors>) => boolean,
): Selector<T, U> {
  let prevDeps: SelectorReturnTypes<T, Selectors> | undefined
  let prevResult: U | undefined

  return (state): U => {
    const currentDeps = selectors.map(fn => fn(state)) as SelectorReturnTypes<T, Selectors>

    if (prevDeps && compare(currentDeps, prevDeps)) {
      return prevResult as U
    }

    const currentResult = resultFn(...currentDeps)

    // Update memoized values
    prevDeps = currentDeps
    prevResult = currentResult

    return currentResult
  }
}

export type MemoizedSelectorHook<U> = (() => U) & {selector: Selector<State, U>}

/**
 * Creates a memoized hook for selecting and computing derived data from the store.
 * Automatically handles both dependency comparison (to avoid recomputing) and shallow
 * comparison of the result (to avoid unnecessary re-renders).
 *
 * This is a convenience wrapper that combines createMemoizedSelector + useShallow + usePullRequestStore.
 *
 * @template Selectors - Array of selector functions that extract values from the state
 * @template U - The type of the final computed result
 *
 * @param selectors - Array of selector functions that pick values from the state
 * @param resultFn - Function that computes the final result using the selected values
 *
 * @returns A hook that returns the computed result
 *
 * @example
 * const useCombinedData = createMemoizedSelectorHook(
 *   [s => s.user, s => s.settings],
 *   (user, settings) => ({ ...user, ...settings })
 * );
 *
 * // Usage in component:
 * const data = useCombinedData();
 */
export function createMemoizedSelectorHook<Selectors extends Array<Selector<State, unknown>>, U>(
  selectors: [...Selectors],
  resultFn: (...args: SelectorReturnTypes<State, Selectors>) => U,
): MemoizedSelectorHook<U> {
  const selector = createMemoizedSelector<State, Selectors, U>(selectors, resultFn, shallow)
  // creating a selector helper
  // eslint-disable-next-line local/no-use-pull-requests-outside-selectors
  const useMemoizedSelectorHook = () => usePullRequestStore(useShallow(selector))
  useMemoizedSelectorHook.selector = selector
  return useMemoizedSelectorHook
}
